import React, { useState, useRef, useEffect } from "react";
import { Upload, Loader2, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, resolveMediaUrl } from "../lib/api";

/**
 * Drop-in image uploader.  Posts to /api/upload/public-doc (anonymous, kind-namespaced)
 * and emits the uploaded URL via the `onUploaded(url)` callback.
 *
 * Props:
 *   - kind         ghana_card | shop_image | hostel_image | room_image | menu_image | selfie
 *   - value        current URL (or empty)
 *   - onUploaded   (url) => void
 *   - label        small label rendered above the picker
 *   - required     boolean — adds a red asterisk
 *   - testId       data-testid prefix (input becomes `${testId}-input`)
 */
export default function ImageUploader({
  kind = "selfie", value = "", onUploaded, onChange, label, required, testId, allowPdf = false,
}) {
  const [busy, setBusy] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileRef = useRef(null);

  const previewUrl = resolveMediaUrl(value);
  useEffect(() => { setPreviewFailed(false); }, [previewUrl]);

  const pick = () => fileRef.current?.click();
  // Notify the parent — supports both `onUploaded(url)` (legacy) and
  // `onChange(url)` (matches the convention used elsewhere in the app).
  const emit = (url) => { onUploaded?.(url); onChange?.(url); };
  const clear = () => emit("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isPdf = file.type === "application/pdf";
    if (!file.type.startsWith("image/") && !(allowPdf && isPdf))
      return toast.error(allowPdf ? "Only image or PDF files allowed" : "Only image files allowed");
    const maxMb = isPdf ? 10 : 8;
    if (file.size > maxMb * 1024 * 1024) return toast.error(`File too large (max ${maxMb} MB)`);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/upload/public-doc?kind=${encodeURIComponent(kind)}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      emit(data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatError(err)); }
    finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-1.5">
      {label && (
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#60727A]">
          {label} {required ? <span className="text-[#EF4444]">*</span> : null}
        </div>
      )}
      <input ref={fileRef} type="file" accept={allowPdf ? "image/*,application/pdf" : "image/*"} hidden onChange={onFile}
             data-testid={testId ? `${testId}-input` : undefined} />

      {previewUrl ? (
        <div className="relative rounded-xl overflow-hidden border border-[#26263A]" data-testid={testId ? `${testId}-preview` : undefined}>
          {previewUrl.toLowerCase().includes(".pdf") ? (
            <div className="w-full h-24 bg-[#F3F8F5] flex items-center justify-center gap-2 text-[#147D60]">
              <ImageIcon size={18} /> <span className="text-xs font-bold">PDF document uploaded</span>
            </div>
          ) : previewFailed ? (
            <div className="w-full h-40 bg-[#F3F8F5] flex flex-col items-center justify-center gap-2 text-[#60727A]">
              <ImageIcon size={22} /> <span className="text-xs font-semibold">Image preview unavailable · replace the file</span>
            </div>
          ) : (
            <img src={previewUrl} alt="" onError={() => setPreviewFailed(true)} className="w-full h-40 object-cover" />
          )}
          <button type="button" onClick={clear}
                  className="absolute top-2 right-2 p-1.5 bg-black/70 hover:bg-[#EF4444] text-white rounded-full"
                  data-testid={testId ? `${testId}-clear` : undefined}>
            <X size={14} />
          </button>
          <button type="button" onClick={pick}
                  data-testid={testId ? `${testId}-replace` : undefined}
                  className="absolute bottom-2 right-2 px-3 py-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[10px] uppercase tracking-wider font-bold rounded-lg inline-flex items-center gap-1">
            <Upload size={11} /> Replace
          </button>
        </div>
      ) : (
        <button type="button" onClick={pick} disabled={busy}
                data-testid={testId ? `${testId}-pick` : undefined}
                className={`w-full h-32 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition ${
                  busy ? "border-[#147D60] bg-[#E8F4ED]" : "border-[#B8CDC2] hover:border-[#147D60] bg-[#F7FAF8]"
                }`}>
          {busy ? (
            <Loader2 className="animate-spin text-[#147D60]" size={18} />
          ) : (
            <>
              <ImageIcon size={18} className="text-[#60727A]" />
              <span className="text-[11px] text-[#60727A]">{allowPdf ? "Tap to upload an image or PDF (max 10 MB)" : "Tap to upload an image (max 8 MB)"}</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}
